import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import useTranslator from "../../hooks/useTranslator";
import {
  Avatar,
  Badge,
  Button,
  Checkbox,
  Icon,
  Modal,
  Notice,
  PublicPage,
  PublicPageStatus,
} from "../../components/design";
import "./SkorProtectPaymentDetails.css";
import { convertCurrencyFormat } from "../../helper/common";

const SKOR_PROTECT_WINDOW_HOURS = 48;

const STATUS = {
  LOADING: "loading",
  DETAILS: "details",
  REDIRECTING: "redirecting",
  SUCCESS: "success",
  FAILURE: "failure",
  ERROR: "error",
};

const TERM_ITEMS = [
  {
    titleKey: "skorIsYourBacking",
    bodyKey: "skorBacksYouWithTheLandlordAndGivesAccessToPrivilegedTerms",
    icon: "shield",
  },
  {
    titleKey: "legalAdvice",
    bodyKey: "questionsAboutYourRightsAsATenant",
    icon: "fileText",
  },
  {
    titleKey: "supportWithYourLandlord",
    bodyKey: "weHelpYouHandleIssuesWithYourLandlord",
    icon: "user",
  },
  {
    titleKey: "buildYourSkorHistory",
    bodyKey: "yourTenantTrackRecordIsLoggedMonthByMonth",
    icon: "calendar",
  },
];

// Short recap shown in the inline dropdown (matches the source HTML's "legal-body" accordion).
const LEGAL_SUMMARY = [
  { titleKey: "legalAgreementIntroTitle", bodyKey: "legalAgreementIntroBody" },
  { titleKey: "startOfCoverage", bodyKey: "startOfCoverageBody" },
  { bodyKey: "tenantsAreJointlyAndSeverallyLiableToSkor" },
  { titleKey: "termAndRenewal", bodyKey: "termAndRenewalBody" },
  {
    titleKey: "instalmentsAndAcceleratedMaturity",
    bodyKey: "instalmentsAndAcceleratedMaturityBody",
  },
  { titleKey: "rightOfWithdrawal", bodyKey: "rightOfWithdrawalBody" },
  {
    titleKey: "commitmentAndCancellation",
    bodyKey: "commitmentAndCancellationBody",
  },
  { titleKey: "backingAndDefault", bodyKey: "backingAndDefaultBody" },
  { titleKey: "limitationOfLiability", bodyKey: "limitationOfLiabilityBody" },
  { bodyKey: "summaryDoesntReplaceFullContractualDocument" },
];

// Full numbered clauses shown in the "Skor Protect Agreement" doc modal.
const CONTRACT_CLAUSES = [
  { titleKey: "natureOfAgreement", bodyKey: "natureOfAgreementBody" },
  { titleKey: "purposeOfAgreement", bodyKey: "purposeOfAgreementBody" },
  { titleKey: "startOfCoverage", bodyKey: "startOfCoverageBody" },
  { bodyKey: "tenantsAreJointlyAndSeverallyLiableToSkor" },
  { titleKey: "servicesProvided", bodyKey: "servicesProvidedBody" },
  { titleKey: "priceAndFees", bodyKey: "priceAndFeesBody" },
  { titleKey: "termAndRenewal", bodyKey: "termAndRenewalBody" },
  {
    titleKey: "instalmentsAndAcceleratedMaturity",
    bodyKey: "instalmentsAndAcceleratedMaturityBody",
  },
  { titleKey: "rightOfWithdrawal", bodyKey: "rightOfWithdrawalBody" },
  {
    titleKey: "commitmentAndCancellation",
    bodyKey: "commitmentAndCancellationBody",
  },
  { titleKey: "backingAndDefault", bodyKey: "backingAndDefaultBody" },
  { titleKey: "limitationOfLiability", bodyKey: "limitationOfLiabilityBody" },
];

const SEPA_ITEMS = [
  { labelKey: "paymentType", valueKey: "recurrentAnnualOrMonthlyCharge" },
  { labelKey: "yourRights", valueKey: "entitledToRefundFromYourBank" },
  {
    labelKey: "uniqueMandateReference",
    valueKey: "mandateReferenceAssignedBeforeFirstDebit",
  },
  {
    labelKey: "revocation",
    valueKey: "canRevokeMandateAnytimeByNotifyingBank",
  },
];

const ACK_KEYS = [
  "instalmentDefaultAuthorizesFullAnnualCharge",
  "canOnlyCancelIfTenancyEndsOrLandlordAuthorizes",
  "skorProtectDoesntExemptMeFromLandlordObligations",
  "ifIStopPayingRentSkorMayClaimAndReportIt",
];

const getTenantName = (tenant) =>
  [tenant?.fname, tenant?.lname].filter(Boolean).join(" ");

const formatCountdown = (totalSeconds) => {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = Math.floor(totalSeconds % 60);
  return `${h}h ${String(m).padStart(2, "0")}m ${String(s).padStart(2, "0")}s`;
};

const SkorProtectPayment = () => {
  const { _t } = useTranslator();
  const navigate = useNavigate();

  const [status, setStatus] = useState(STATUS.LOADING);
  const [errorMessage, setErrorMessage] = useState("");
  const [token, setToken] = useState(null);
  const [details, setDetails] = useState(null);
  const [consentChecked, setConsentChecked] = useState(false);
  const [consentTimestamp, setConsentTimestamp] = useState(null);
  const [legalOpen, setLegalOpen] = useState(false);
  const [openDoc, setOpenDoc] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get("token");
    const paymentStatus = params.get("status");

    window.history.replaceState({}, "", window.location.pathname);

    if (paymentStatus === "success") {
      setStatus(STATUS.SUCCESS);
      return;
    }

    if (paymentStatus === "failure") {
      setStatus(STATUS.FAILURE);
      return;
    }

    if (!urlToken) {
      setStatus(STATUS.ERROR);
      setErrorMessage(_t("invalidOrExpiredPaymentLink"));
      return;
    }

    setToken(urlToken);
    fetchDetails(urlToken);
  }, []);

  useEffect(() => {
    if (status !== STATUS.DETAILS || !details?.skor_protect_initiated) {
      return undefined;
    }

    const deadlineMs =
      new Date(details.skor_protect_initiated).getTime() +
      SKOR_PROTECT_WINDOW_HOURS * 60 * 60 * 1000;

    const computeRemaining = () =>
      Math.max(0, Math.floor((deadlineMs - Date.now()) / 1000));

    setSecondsLeft(computeRemaining());
    const interval = setInterval(() => {
      setSecondsLeft(computeRemaining());
    }, 1000);

    return () => clearInterval(interval);
  }, [status, details]);

  const fetchDetails = async (tokenValue) => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/skor-protect/details`,
        {
          params: { token: tokenValue },
          headers: {
            "Accept-Language": localStorage.getItem("i18nextLng") || "en",
          },
        }
      );

      setDetails(res.data?.success_res || {});
      setStatus(STATUS.DETAILS);
    } catch (err) {
      setStatus(STATUS.ERROR);
      setErrorMessage(_t("paymentLinkIsInvalidOrExpired"));
    }
  };

  const initiatePayment = async (tokenValue) => {
    setStatus(STATUS.REDIRECTING);

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/skor-protect/initiate-payment`,
        { token: tokenValue },
        {
          headers: {
            "Accept-Language": localStorage.getItem("i18nextLng") || "en",
          },
        }
      );

      const paymentUrl = res.data?.success_res?.paymentUrl;

      if (!paymentUrl) {
        setStatus(STATUS.ERROR);
        setErrorMessage(_t("paymentLinkNotReceived"));
        return;
      }

      window.location.href = paymentUrl;
    } catch (err) {
      setStatus(STATUS.ERROR);
      setErrorMessage(_t("paymentLinkIsInvalidOrExpired"));
    }
  };

  const handleContinueToPayment = () => {
    if (!consentChecked || !token) return;
    initiatePayment(token);
  };

  const goToProfile = () => {
    navigate("/home");
  };

  if (status === STATUS.LOADING) {
    return (
      <PublicPage width="sm">
        <PublicPageStatus title={_t("pleaseWait")} />
      </PublicPage>
    );
  }

  if (status === STATUS.REDIRECTING) {
    return (
      <PublicPage width="sm">
        <PublicPageStatus
          title={_t("redirectingToPayment")}
          description={_t("pleaseWaitWhileWeSecurelyProcessYourPayment")}
        />
      </PublicPage>
    );
  }

  if (status === STATUS.SUCCESS || status === STATUS.FAILURE) {
    const isSuccess = status === STATUS.SUCCESS;
    return (
      <PublicPage width="sm">
        <PublicPageStatus
          tone={isSuccess ? "success" : "danger"}
          title={_t(isSuccess ? "paymentSuccessful" : "paymentNotCompleted")}
          description={_t(
            isSuccess
              ? "yourPaymentHasBeenCompletedSuccessfully"
              : "yourPaymentCouldNotBeCompletedPleaseTryAgain"
          )}
        >
          <Button size="lg" block onClick={goToProfile}>
            {_t("goToMyProfile")}
          </Button>
        </PublicPageStatus>
      </PublicPage>
    );
  }

  if (status === STATUS.DETAILS) {
    const tenants = [...(details?.selected_tenants || [])].sort((a, b) => {
      const aIsPayer = String(a._id) === String(details?.tenant_id);
      const bIsPayer = String(b._id) === String(details?.tenant_id);
      return aIsPayer === bIsPayer ? 0 : aIsPayer ? -1 : 1;
    });

    const steps = [
      { label: _t("approval"), state: "done" },
      { label: _t("terms"), state: "now" },
      { label: _t("payment"), state: "next" },
    ];

    return (
      <PublicPage width="md">
        <div className="skpd">
          <ol className="skpd__steps">
            {steps.map((step, idx) => (
              <li key={idx} className={`skpd__step is-${step.state}`}>
                <span className="skpd__step-dot">
                  {step.state === "done" ? (
                    <Icon name="check" size={14} />
                  ) : (
                    idx + 1
                  )}
                </span>
                {step.label}
              </li>
            ))}
          </ol>

          {secondsLeft !== null && (
            <Notice
              tone="warning"
              action={
                <strong className="skpd__timer">
                  {formatCountdown(secondsLeft)}
                </strong>
              }
            >
              {_t("yourLandlordAcceptedYourSkorProtect")}
            </Notice>
          )}

          <div className="skpd__hero">
            <Badge>{_t("theLandlordHasAcceptedYou")}</Badge>
            <h1 className="ds-public-page__title">
              {_t("confirmYourSkorProtectSeal")}
            </h1>
            <p className="ds-public-page__description">
              <u>{_t("landlordReviewedYourProfileAndAcceptedSkorProtect")}</u>{" "}
              {_t("completeNowToKeepYourCandidacyActive")}
            </p>
          </div>

          <div className="skpd__block">
            <h2 className="skpd__heading">{_t("howSkorBacksYourLease")}</h2>
            <p className="skpd__sub">
              {_t("reassuranceOfHavingSomeoneVouchForYou")}
            </p>
            <p className="skpd__text">
              {_t("skorProtectIsYourSealOfQualityWithTheLandlord")}
            </p>
            <p className="skpd__strong">
              {_t("skorProtectActivatesWhenLeaseIsSigned")}
            </p>
            <p className="skpd__sub">
              {_t("skorBacksYouForTheWholeLifeOfYourLease")}
            </p>
            <p className="skpd__text">
              {_t("ifYouDontSignTheLeaseWeRefundYouInFull")}
            </p>
          </div>

          {tenants.length > 0 && (
            <div className="skpd__block">
              <h2 className="skpd__heading">{_t("coveredTenants")}</h2>
              <p className="skpd__sub">{_t("coveredByThisSamePayment")}</p>
              <Notice>
                {_t("paymentByAnyTenantActivatesCoverageForEveryone")}
              </Notice>
              <ul className="skpd__people">
                {tenants.map((tenant, idx) => {
                  const tenantName = getTenantName(tenant);
                  const isPayer =
                    details?.tenant_id &&
                    String(tenant._id) === String(details.tenant_id);

                  return (
                    <li key={tenant._id || idx}>
                      <Avatar name={tenantName} />
                      <span className="skpd__person">
                        <span className="skpd__person-name">{tenantName}</span>
                        {tenant.email && (
                          <span className="skpd__person-email">
                            {tenant.email}
                          </span>
                        )}
                      </span>
                      {isPayer && <Badge>{_t("youArePaying")}</Badge>}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          <div className="skpd__block">
            <h2 className="skpd__heading">
              {_t("whatDoesSkorProtectInclude")}
            </h2>
            <p className="skpd__sub">
              {_t("skorBacksYouAsATenantForTheWholeLease")}
            </p>
            <ul className="skpd__terms">
              {TERM_ITEMS.map((term) => (
                <li key={term.titleKey}>
                  <Icon name={term.icon} size={20} />
                  <span>
                    <span className="skpd__strong">{_t(term.titleKey)}</span>
                    <span className="skpd__text">{_t(term.bodyKey)}</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <div className="skpd__block">
            <Button
              variant="secondary"
              block
              iconStart={<Icon name="fileText" size={16} />}
              iconEnd={
                <Icon name={legalOpen ? "minus" : "chevronDown"} size={16} />
              }
              onClick={() => setLegalOpen((open) => !open)}
            >
              {_t("viewTheFullServiceAgreement")}
            </Button>
            {legalOpen && (
              <div className="skpd__legal">
                {LEGAL_SUMMARY.map((clause, idx) => (
                  <p key={idx}>
                    {clause.titleKey && <strong>{_t(clause.titleKey)} </strong>}
                    {_t(clause.bodyKey)}
                  </p>
                ))}
              </div>
            )}
          </div>

          <div className="skpd__block">
            <ul className="skpd__acks">
              {ACK_KEYS.map((key) => (
                <li key={key}>
                  <Icon name="check" size={16} />
                  <span>{_t(key)}</span>
                </li>
              ))}
            </ul>

            <Checkbox
              checked={consentChecked}
              onChange={(next) => {
                setConsentChecked(next);
                setConsentTimestamp(next ? new Date().toISOString() : null);
              }}
              label={
                <>
                  {_t("iHaveReadAndAcceptThe")}{" "}
                  <a
                    href="#skor-protect-agreement"
                    className="skpd__link"
                    onClick={(e) => {
                      e.preventDefault();
                      setOpenDoc("contract");
                    }}
                  >
                    {_t("skorProtectAgreement")}
                  </a>{" "}
                  {_t("andThe")}{" "}
                  <a
                    href="#sepa-direct-debit-mandate"
                    className="skpd__link"
                    onClick={(e) => {
                      e.preventDefault();
                      setOpenDoc("sepa");
                    }}
                  >
                    {_t("sepaDirectDebitMandate")}
                  </a>
                  , {_t("includingThePaymentAndSubrogationConditionsAbove")}
                </>
              }
            />

            {consentChecked && consentTimestamp && (
              <p className="skpd__timestamp">
                {_t("timestamp")}: {consentTimestamp}
              </p>
            )}
          </div>

          <div className="skpd__pay">
            {details?.skor_protect_amount != null && (
              <div className="skpd__price">
                <span className="skpd__amount">
                  {convertCurrencyFormat(details.skor_protect_amount)}€
                </span>
                <span className="skpd__sub">{_t("today")}</span>
              </div>
            )}
            <Button
              size="lg"
              block
              iconStart={<Icon name="lock" size={16} />}
              disabled={!consentChecked}
              onClick={handleContinueToPayment}
            >
              {_t("proceedWithPayment")}
            </Button>
          </div>
        </div>

        <Modal
          open={Boolean(openDoc)}
          onClose={() => setOpenDoc(null)}
          size="md"
          align="start"
          actionsLayout="end"
          title={
            openDoc === "contract"
              ? _t("professionalServicesAgreement")
              : _t("sepaDirectDebitMandate")
          }
          description={
            openDoc === "contract" ? (
              _t("madrentVenturesTaxIdVersion")
            ) : (
              <>
                {_t("creditor")}: MADRENT VENTURES, S.L. — {_t("taxId")}{" "}
                B56523731
              </>
            )
          }
          actions={
            <Button size="lg" onClick={() => setOpenDoc(null)}>
              {_t("close")}
            </Button>
          }
        >
          <div className="skpd__doc">
            {openDoc === "contract" && (
              <>
                {CONTRACT_CLAUSES.map((clause, idx) => (
                  <p key={idx}>
                    {clause.titleKey && <strong>{_t(clause.titleKey)} </strong>}
                    {_t(clause.bodyKey)}
                  </p>
                ))}
                <p>{_t("summaryDoesntReplaceFullContractualDocument")}</p>
              </>
            )}
            {openDoc === "sepa" && (
              <>
                <p>{_t("sepaMandateIntro")}</p>
                <p>
                  <strong>{_t("creditor")} IBAN:</strong> ES80 0182 0901 6102
                  0239 0189 — BBVA
                </p>
                {SEPA_ITEMS.map((item, idx) => (
                  <p key={idx}>
                    <strong>{_t(item.labelKey)}:</strong> {_t(item.valueKey)}
                  </p>
                ))}
              </>
            )}
          </div>
        </Modal>
      </PublicPage>
    );
  }

  return (
    <PublicPage width="sm">
      <PublicPageStatus
        tone="danger"
        title={_t("unableToProcessPayment")}
        description={errorMessage}
      >
        <Button size="lg" block onClick={goToProfile}>
          {_t("goToMyProfile")}
        </Button>
      </PublicPageStatus>
    </PublicPage>
  );
};

export default SkorProtectPayment;
